export const EMAIL_PROVIDERS = Object.freeze([
  { id: "gmail", label: "Gmail / Google Workspace" },
  { id: "outlook", label: "Outlook.com" },
  { id: "microsoft365", label: "Microsoft 365 (work or school)" },
  { id: "yahoo", label: "Yahoo Mail" },
  { id: "system", label: "Default email app" },
]);

export function isEmailProvider(value) {
  return EMAIL_PROVIDERS.some((provider) => provider.id === value);
}

export function emailProviderStorageKey(accountId) {
  return `sbs.emailProvider.v1:${accountId || "guest"}`;
}

export function readEmailProvider(accountId, storage) {
  try {
    const value = (storage || window.localStorage).getItem(emailProviderStorageKey(accountId));
    return isEmailProvider(value) ? value : "";
  } catch {
    return "";
  }
}

export function writeEmailProvider(accountId, provider, storage) {
  if (provider && !isEmailProvider(provider)) return false;
  try {
    const key = emailProviderStorageKey(accountId);
    const target = storage || window.localStorage;
    if (provider) target.setItem(key, provider);
    else target.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function gmailAccountStorageKey(accountId) {
  return `sbs.gmailAccount.v1:${accountId || "guest"}`;
}

export function isGmailAccountChoice(value) {
  return value?.mode === "choose" || (value?.mode === "address"
    && typeof value.email === "string" && value.email.length <= 254
    && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(value.email));
}

export function readGmailAccount(accountId, storage) {
  try {
    const value = JSON.parse((storage || window.localStorage).getItem(gmailAccountStorageKey(accountId)));
    return isGmailAccountChoice(value) ? { mode: value.mode, email: value.mode === "address" ? value.email : "" } : null;
  } catch {
    return null;
  }
}

export function writeGmailAccount(accountId, choice, storage) {
  if (!isGmailAccountChoice(choice)) return false;
  try {
    (storage || window.localStorage).setItem(gmailAccountStorageKey(accountId), JSON.stringify({
      mode: choice.mode, email: choice.mode === "address" ? choice.email : "",
    }));
    return true;
  } catch {
    return false;
  }
}

export function buildEmailComposeUrl(provider, { recipientEmail = "", subject = "", body = "" } = {}, gmailAccount) {
  if (!isEmailProvider(provider)) throw new Error("Choose an email provider.");
  const recipient = encodeURIComponent(String(recipientEmail || "").trim().replace(/[\r\n]/g, ""));
  const heading = encodeURIComponent(String(subject || "").replace(/[\r\n]+/g, " "));
  const message = encodeURIComponent(String(body || ""));
  if (provider === "system") return `mailto:${recipient}?subject=${heading}&body=${message}`;
  if (provider === "gmail") {
    // Google owns its signed-in account list. Use its chooser unless the user
    // explicitly selected a Google sign-in address; never assume account /u/0.
    if (gmailAccount && !isGmailAccountChoice(gmailAccount)) throw new Error("Enter a valid Google account email address.");
    const account = gmailAccount?.mode === "address" ? `&authuser=${encodeURIComponent(gmailAccount.email)}` : "";
    const compose = `https://mail.google.com/mail/?view=cm&fs=1&to=${recipient}&su=${heading}&body=${message}${account}`;
    return account ? compose : `https://accounts.google.com/AccountChooser?service=mail&continue=${encodeURIComponent(compose)}`;
  }
  if (provider === "yahoo") return `https://compose.mail.yahoo.com/?to=${recipient}&subject=${heading}&body=${message}`;
  const host = provider === "microsoft365" ? "outlook.office.com" : "outlook.live.com";
  return `https://${host}/mail/deeplink/compose?to=${recipient}&subject=${heading}&body=${message}`;
}
